/**
 * SLEEPSAKE (DEV-858): notes on the cart which cart drawer the shopper saw,
 * `cart_drawer: reserved` or `classic`, so orders can be split by side of the
 * Australian Rollouts experiment (orders, order value, bundles, refunds),
 * which Rollouts' own analytics don't break down. Shopify carries cart
 * attributes onto the order's "Additional details"; it is not an order tag.
 * Same mechanism as the bonus gift card's `bonus_gift_card` note.
 *
 * The tag that loads this module (`snippets/cart-drawer-note.liquid`) says
 * which drawer this page shows and what the cart already holds, so a cart
 * that already carries the right note costs no request.
 */

/** Cart attribute name. A contract with the order read; never rename it mid-experiment. */
export const NOTE_ATTRIBUTE = "cart_drawer";

/**
 * The note to write, or null when there's nothing to do: the cart is empty,
 * the page doesn't know its drawer, or the cart already says so.
 * @param {{ version: string | null, current: string, itemCount: number }} state
 * @returns {string | null}
 */
export function noteToWrite({ version, current, itemCount }) {
  if (version !== "reserved" && version !== "classic") return null;
  if (!(itemCount > 0)) return null;
  return current === version ? null : version;
}

/**
 * The cart's item count from a `cart:update` event, or null when the event
 * doesn't say (error paths carry an empty resource).
 * @param {{ resource?: { item_count?: number }, data?: { itemCount?: number } } | undefined} detail
 * @returns {number | null}
 */
export function countFromEvent(detail) {
  const count = detail?.resource?.item_count ?? detail?.data?.itemCount;
  return Number.isFinite(count) ? count : null;
}

/**
 * The `/cart/update` body: the note and nothing else, so the request can
 * never touch line quantities.
 * @param {string} note
 */
export function noteRequestBody(note) {
  return { attributes: { [NOTE_ATTRIBUTE]: note } };
}

/** Failed writes before the page gives up, so a store outage costs two requests, not one per cart change. */
const MAX_FAILURES = 2;

if (typeof document !== "undefined") {
  const tag = document.querySelector("script[data-cart-drawer-version]");
  const version = tag?.getAttribute("data-cart-drawer-version") ?? null;
  let current = tag?.getAttribute("data-cart-drawer-note") ?? "";
  let itemCount = Number(tag?.getAttribute("data-cart-count")) || 0;
  /** @type {Promise<void> | null} */
  let inFlight = null;
  let failures = 0;

  const write = () => {
    const note = noteToWrite({ version, current, itemCount });
    if (note === null || inFlight || failures >= MAX_FAILURES) return;
    inFlight = fetch(Theme.routes.cart_update_url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(noteRequestBody(note)),
      // No `keepalive`: the browser caps a page's in-flight keepalive bytes
      // (64 KB) across every script, and analytics beacons fill it at load,
      // so a keepalive write is refused outright and the note goes missing.
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
      .then((cart) => {
        // A 2xx means the note is on the cart, so it isn't written again; a
        // reply that doesn't echo it still counts towards the failure cap.
        current = note;
        if (cart?.attributes?.[NOTE_ATTRIBUTE] !== note) failures += 1;
      })
      // A failed write leaves the order untagged; the cart itself is untouched.
      .catch(() => {
        failures += 1;
      })
      .finally(() => {
        inFlight = null;
      });
  };

  // Any cart change, not only an add: a cart from before this shipped gets
  // its note on the next change. The event name is `ThemeEvents.cartUpdate`,
  // spelt out so this module needs no import and stays testable under Node.
  document.addEventListener("cart:update", (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail?.data?.didError) return;
    const count = countFromEvent(detail);
    if (count !== null) itemCount = count;
    write();
  });

  write();
}
